import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TableToolbar,
  TableToolbarActions,
  Text,
} from '@razorpay/blade/components';
import { getErrorsAsString } from 'components/Kyc/utils';
import { routePaths } from 'components/Routes/data';
import { SalaryStructure } from 'components/SalaryStructure/types';
import { useFilters } from 'hooks/useFilters';
import { usePagination } from 'hooks/usePagination';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { AppError } from 'utils/AppError';
import {
  SalaryStructuresListEmptyStateView,
  SalaryStructuresListErrorStateView,
  SalaryStructuresListTableContent,
  SalaryStructuresListToolbar,
} from './components';
import { StructureCreateType } from './creation';

interface SalaryStructureListViewProps {
  structures: SalaryStructure[];
  isFetching: boolean;
  onRetry: () => void;
  error: unknown;
  onCreateStructure: (createType: StructureCreateType) => void;
}

/**
 * SalaryStructureListView component that displays a list of salary structures
 * with different views for empty, error, and data states.
 *
 * All UI components have been extracted to separate files for better organization
 * and maintainability.
 */
const SalaryStructureListView: React.FC<SalaryStructureListViewProps> = ({
  structures,
  isFetching,
  onRetry,
  error,
  onCreateStructure,
}) => {
  const history = useHistory();
  const isError = Boolean(error);

  // Pagination state
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  // Filters state
  const { isAnyFilterApplied } = useFilters({
    filtersMap: {
      status: 'status',
      search: 'search',
    },
  });

  // Derived state
  const isTableEmpty = structures.length === 0;
  const errorString = getErrorsAsString(error as AppError);

  // Event handlers
  const handleViewDetails = (structureId: string) => {
    history.push(`${routePaths.salaryStructures}/${structureId}`);
  };

  // Handle different view states with separate return statements

  // 1. Error state
  if (isError) {
    return <SalaryStructuresListErrorStateView errorString={errorString} onRetry={onRetry} />;
  }

  // 2. Empty state (when there's no data and not loading)
  if (isTableEmpty && !isFetching) {
    return (
      <SalaryStructuresListEmptyStateView
        isAnyFilterApplied={isAnyFilterApplied}
        onCreateClick={() => onCreateStructure('new')}
      />
    );
  }

  // 3. Normal table view (with or without data)
  return (
    <Table
      data={{ nodes: structures }}
      isLoading={isFetching}
      pagination={
        !isTableEmpty ? (
          <TablePagination
            currentPage={currentPage}
            showPageSizePicker={true}
            onPageChange={({ page }) => setCurrentPage(page)}
            onPageSizeChange={({ pageSize }) => setItemsPerPage(pageSize)}
            totalItemCount={structures.length}
          />
        ) : undefined
      }
      toolbar={
        <TableToolbar
          title={`Showing ${currentPage * itemsPerPage + 1}-${Math.min(
            (currentPage + 1) * itemsPerPage,
            structures.length,
          )} of ${structures.length} Salary Structures`}>
          <TableToolbarActions>
            <SalaryStructuresListToolbar onRefresh={onRetry} onCreateNew={onCreateStructure} />
          </TableToolbarActions>
        </TableToolbar>
      }>
      {(tableData) => {
        // The tableData parameter contains the actual data from the Table
        // that we need to render with our custom components
        return (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  <Text weight="semibold">Template name</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold">Description</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold">Status</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold">No. of employees</Text>
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>

            <SalaryStructuresListTableContent
              structures={tableData}
              onViewDetails={handleViewDetails}
            />
          </>
        );
      }}
    </Table>
  );
};

export default SalaryStructureListView;
